import { Pencil } from '@factorialco/f0-react/icons/app'
import DOMPurify from 'dompurify'
import {
  FormFragment,
  SurveysHeaderTrainingClassFragment,
  TrainingSurveyRespondentsFragment,
} from 'generated/resources/graphql'

import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

type Variant = 'positive' | 'neutral' | 'critical' | 'info'

type Props = {
  form: FormFragment
  groups: SurveysHeaderTrainingClassFragment[]
  respondents: TrainingSurveyRespondentsFragment[] | []
  trainingStatus: string
}

const getStatusDetails = (status: string): { text: string; variant: Variant } => {
  if (status === 'active') {
    return {
      text: i18n.t('trainings.surveys.status.sent'),
      variant: 'positive',
    }
  }
  return {
    text: i18n.t('trainings.surveys.status.draft'),
    variant: 'neutral',
  }
}

export const buildOverviewFormHeaderProps = (data: Props) => {
  const form = data?.form
  if (!form) {
    return {
      title: i18n.t('trainings.surveys.fallback_title'),
    }
  }

  return {
    title: form.title,
    description: DOMPurify.sanitize(form.description ?? '', { ALLOWED_TAGS: [] }),
    status: {
      label: '',
      ...getStatusDetails(form.status),
    },
    metadata: [
      {
        label: i18n.t('forms.header.current_survey'),
        value: {
          type: 'text' as const,
          content: stringToDate(form.createdAt, 'backend_datetime').format('LL'),
        },
      },
    ],
    primaryAction: {
      key: 'send',
      label: i18n.t('common.send'),
      disabled: data.trainingStatus !== 'active',
    },
    secondaryActions: [
      {
        key: 'edit',
        icon: Pencil,
        label: i18n.t('trainings.surveys.create.edit_content'),
        disabled: form.status !== 'draft',
        loading: true,
      },
    ],
    otherActions: (() => {
      const isDraft = form.status === 'draft'
      const actions = []

      actions.push({
        key: 'save_template',
        label: i18n.t('trainings.surveys.create.dropdown.save_as_template'),
      })

      if (!isDraft) return actions

      return [
        ...actions,
        {
          key: 'edit_settings',
          label: i18n.t('trainings.surveys.create.dropdown.edit'),
        },
        {
          key: 'delete',
          label: i18n.t('trainings.trainings_table.delete'),
        },
      ]
    })(),
  }
}
